"use client"

import { useState, useEffect } from "react"
import {
  createClient,
  hasConfiguredBrowserSupabaseEnv,
  hasPlaywrightAuthE2ESession,
} from "@/lib/supabase/client"
import { fetchWorldBootstrap } from "@/lib/backend/world"
import { fetchPlatformDashboard } from "@/lib/backend/platform-core"

const SESSION_STATUS_TIMEOUT_MS = 1200

export interface ConnectedAgent {
  agent_id: string
  name: string | null
  is_default: boolean
}

interface ConnectionState {
  ready: boolean
  connected: boolean
  agents: ConnectedAgent[]
  defaultAgent: string
  accessToken: string | null
}

async function getSessionSnapshot(supabase: ReturnType<typeof createClient>) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  try {
    return await Promise.race([
      supabase.auth.getSession(),
      new Promise<{ data: { session: null }; error: null }>((resolve) => {
        timeoutId = setTimeout(() => {
          resolve({
            data: { session: null },
            error: null,
          })
        }, SESSION_STATUS_TIMEOUT_MS)
      }),
    ])
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
  }
}

export function useOpenClawConnection(options?: {
  skip?: boolean
  /**
   * When true, resolve only the shared world-bootstrap connection status and
   * skip the dashboard call (agents list). Faster and lighter — use when the
   * caller only needs `ready` and `connected`, not the full agents list.
   */
  minimal?: boolean
}): ConnectionState {
  const shouldSkip =
    Boolean(options?.skip) ||
    (!hasConfiguredBrowserSupabaseEnv() && !hasPlaywrightAuthE2ESession())
  const minimal = Boolean(options?.minimal)

  const [state, setState] = useState<ConnectionState>({
    ready: false,
    connected: false,
    agents: [],
    defaultAgent: "main",
    accessToken: null,
  })

  useEffect(() => {
    if (shouldSkip) {
      return
    }

    let cancelled = false

    async function init() {
      const supabase = createClient()
      const {
        data: { session },
      } = await getSessionSnapshot(supabase)
      // IMPORTANT: cancelled means this effect invocation was superseded (e.g.
      // Strict Mode dev double-invocation). Must NOT touch state, or we commit
      // a half-resolved ready=true while the real invocation is still fetching
      // — which makes the loading spinner skip and the Connect button flash.
      if (cancelled) return

      try {
        const accessToken = session?.access_token ?? null
        const bootstrap = await fetchWorldBootstrap({
          accessToken,
          waitForSessionMs: 0,
        })
        if (cancelled) return
        if (!bootstrap?.openclaw_connected) {
          setState({
            ready: true,
            connected: false,
            agents: [],
            defaultAgent: "main",
            accessToken,
          })
          return
        }

        if (minimal) {
          setState({
            ready: true,
            connected: true,
            agents: [],
            defaultAgent: "main",
            accessToken,
          })
          return
        }

        if (!accessToken) {
          setState({
            ready: true,
            connected: true,
            agents: [],
            defaultAgent: "main",
            accessToken: null,
          })
          return
        }

        const dashboard = await fetchPlatformDashboard(accessToken)
        if (cancelled) return

        const agents = dashboard.agents.map((agent) => ({
          agent_id: agent.agent_id,
          name: agent.name,
          is_default: agent.is_default,
        })) as ConnectedAgent[]
        const defaultAgent =
          agents.find((a) => a.is_default)?.agent_id ??
          agents[0]?.agent_id ??
          "main"

        setState({
          ready: true,
          connected: true,
          agents,
          defaultAgent,
          accessToken,
        })
      } catch {
        if (!cancelled) {
          setState({
            ready: true,
            connected: false,
            agents: [],
            defaultAgent: "main",
            accessToken: session?.access_token ?? null,
          })
        }
      }
    }

    init()
    return () => {
      cancelled = true
    }
  }, [minimal, shouldSkip])

  if (shouldSkip) {
    return {
      ready: true,
      connected: false,
      agents: [],
      defaultAgent: "main",
      accessToken: null,
    }
  }

  return state
}
